import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useAppSelector } from '../../store/hooks';

interface CreateEditDocumentTypeModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: DocumentTypeFormData) => void;
  onDelete?: () => void;
  mode: 'create' | 'edit';
  saving?: boolean;
  deleting?: boolean;
  saveError?: string | null;
}

export interface DocumentTypeFormData {
  id?: string;
  code: string;
  name: string;
  description: string;
  chunkSize: number;
  chunkOverlap: number;
}

const CreateEditDocumentTypeModal: React.FC<CreateEditDocumentTypeModalProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  mode,
  saving = false,
  deleting = false,
  saveError = null,
}) => {
  const { selectedDocumentType, loading } = useAppSelector((state) => state.documentTypes);

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [chunkSize, setChunkSize] = useState<number>(1800);
  const [chunkOverlap, setChunkOverlap] = useState<number>(300);

  useEffect(() => {
    if (mode === 'edit' && selectedDocumentType) {
      setCode(selectedDocumentType.code);
      setName(selectedDocumentType.name);
      setDescription(selectedDocumentType.description);
      setChunkSize(selectedDocumentType.chunkSize);
      setChunkOverlap(selectedDocumentType.chunkOverlap);
    } else if (mode === 'create') {
      // Limpiar el formulario para creación
      setCode('');
      setName('');
      setDescription('');
      setChunkSize(1800);
      setChunkOverlap(300);
    }
  }, [mode, selectedDocumentType, open]);

  const handleSave = () => {
    const formData: DocumentTypeFormData = {
      code,
      name,
      description,
      chunkSize,
      chunkOverlap,
    };

    if (mode === 'edit' && selectedDocumentType) {
      formData.id = selectedDocumentType.id;
    }

    onSave(formData);
  };

  const handleClose = () => {
    onClose();
  };

  const isFormValid = code && name && description && chunkSize > 0 && chunkOverlap >= 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === 'create' ? 'Crear Nuevo Tipo de Documento' : 'Editar Tipo de Documento'}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            {saveError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {saveError}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Código"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              margin="normal"
              required
              disabled={saving}
              helperText="Código único para identificar el tipo de documento"
            />

            <TextField
              fullWidth
              label="Nombre"
              value={name}
              onChange={(e) => setName(e.target.value)}
              margin="normal"
              required
              disabled={saving}
            />

            <TextField
              fullWidth
              label="Descripción"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              margin="normal"
              multiline
              rows={3}
              required
              disabled={saving}
            />

            <TextField
              fullWidth
              label="Chunk Size"
              type="number"
              value={chunkSize}
              onChange={(e) => setChunkSize(Number(e.target.value))}
              margin="normal"
              required
              disabled={saving}
              helperText="Tamaño del fragmento para procesamiento"
            />

            <TextField
              fullWidth
              label="Chunk Overlap"
              type="number"
              value={chunkOverlap}
              onChange={(e) => setChunkOverlap(Number(e.target.value))}
              margin="normal"
              required
              disabled={saving}
              helperText="Superposición entre fragmentos"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {mode === 'edit' && onDelete && (
          <Button
            onClick={onDelete}
            variant="outlined"
            color="error"
            disabled={saving || deleting}
          >
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleClose} variant="outlined" disabled={saving || deleting}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || saving || deleting || !isFormValid}
        >
          {saving ? 'Guardando...' : mode === 'create' ? 'Crear' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEditDocumentTypeModal;
